import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { ProductChange } from "../models/ProductChange";
import { useTableData } from "../hooks/useTableData";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

interface ProductChangesTableProps {
	productChanges: ProductChange[];
	selectedChange: ProductChange | null;
	onRowSelected: (change: ProductChange | null) => void;
	onDelete: (change: ProductChange) => void;
}

export const ProductChangesTable = ({
	productChanges,
	selectedChange,
	onRowSelected,
	onDelete,
}: ProductChangesTableProps) => {
	const gridRef = useRef<AgGridReact<ProductChange>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback(
		(_params: GridReadyEvent<ProductChange>) => {},
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<ProductChange>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<ProductChange>(onRowSelected);

	const getRowId = useCallback((params: { data: ProductChange }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<ProductChange>
				ref={gridRef}
				rowData={productChanges}
				columnDefs={columnDefs}
				defaultColDef={listColDef(globalDefaultColDef)}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				groupHeaderHeight={LIST_HEADER_HEIGHT}
				getRowStyle={(params) => {
					if (selectedChange?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
